import React from 'react';
import PropTypes from 'prop-types';
import '../../../component-styles/InvoicePdfDocument.css';
import { depositRequestLabel, depositStillDue, formatInvoiceDate } from '../../../utils/invoiceAdjustments.js';

function fmt(value) {
  return Number(value || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function formatDate(value) {
  if (!value) return '';
  return formatInvoiceDate(value) || String(value).slice(0, 10);
}

function splitAddress(value) {
  return String(value || '')
    .split(/\r?\n|,\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function getImageUrl(item) {
  return item?.imageUrl || item?.image || item?.photoUrl || '';
}

function proxyImg(url, apiBase) {
  if (!url || !apiBase) return url;
  return `${apiBase}/api/upload/proxy-image?url=${encodeURIComponent(url)}`;
}

// Rounded per line like the saved lineMerch, so the line totals add up to Sub Total.
function lineMerchandise(item) {
  return Number((Number(item?.clientUnit || 0) * Number(item?.qty || 0)).toFixed(2));
}

export default function InvoicePdfDocument({ invoice, company, apiBase, template }) {
  const showRoomColumn = template?.showRoomColumn !== false;
  const showItemImages = template?.showItemImages !== false;
  const footerText = template?.footerText || 'Invoice provided by InteriorFolio';
  const termsText = template?.termsText || '';
  const rootStyle = template?.accentColor ? { '--if-green': template.accentColor } : undefined;

  const lineItems = Array.isArray(invoice?.lineItems) ? invoice.lineItems : [];
  const totals = invoice?.totals || {};
  const createdDate = invoice?.invoiceDate || invoice?.createdAt || invoice?.updatedAt || invoice?.date;

  const merchandise = lineItems.reduce((sum, item) => sum + lineMerchandise(item), 0);
  const freightTaxable = lineItems.reduce((sum, item) => {
    if (!item?.shippingTaxable) return sum;
    return sum + Number(item?.clientShipping ?? item?.shipping ?? 0);
  }, 0);
  const freightNonTaxable = lineItems.reduce((sum, item) => {
    if (item?.shippingTaxable) return sum;
    return sum + Number(item?.clientShipping ?? item?.shipping ?? 0);
  }, 0);
  const freightTotal = freightTaxable + freightNonTaxable;
  const tariffTaxable = lineItems.reduce((sum, item) => {
    if (!item?.tariffTaxable) return sum;
    return sum + Number(item?.clientTariff ?? item?.tariff ?? 0);
  }, 0);
  const tariffNonTaxable = lineItems.reduce((sum, item) => {
    if (item?.tariffTaxable) return sum;
    return sum + Number(item?.clientTariff ?? item?.tariff ?? 0);
  }, 0);
  const tariffTotal = tariffTaxable + tariffNonTaxable;
  const summary = {
    merchandise,
    freightTaxable,
    freightNonTaxable,
    freightTotal,
    tariffTaxable,
    tariffNonTaxable,
    tariffTotal,
    tax: Number(totals.tax || 0),
    total: Number(totals.total ?? merchandise + freightTotal + tariffTotal + Number(totals.tax || 0)),
    paid: Number(totals.paid || 0),
    remaining: 0,
  };
  summary.remaining = Number(invoice?.remainingBalance ?? Math.max(summary.total - summary.paid, 0));
  const depositRequested = Number(totals.depositRequested || 0);
  const depositDue = summary.remaining > 0 ? depositStillDue(depositRequested, summary.paid) : 0;

  const logoUrl = proxyImg(company?.logoUrl || invoice?.companyLogoUrl, apiBase);
  const companyName = company?.companyName || invoice?.companyName || 'InteriorFolio';
  const companyLines = splitAddress(company?.address);
  const billingLines = splitAddress(invoice?.billingAddress);
  const shippingLines = splitAddress(invoice?.shippingAddress || invoice?.projectSite);
  const clientName = invoice?.client?.name || invoice?.customerName || '';
  const invoiceNumber = invoice?.invoiceNumber || invoice?.invoiceId || '-';

  return (
    <div className="inv-pdf" style={rootStyle}>
      {/* ── HEADER ── */}
      <header className="inv-pdf__header">
        <div className="inv-pdf__brand">
          {logoUrl && <img className="inv-pdf__logo" src={logoUrl} alt="Company logo" crossOrigin="anonymous" />}
          <div className="inv-pdf__company-info">
            <div className="inv-pdf__company-name">{companyName}</div>
            {companyLines.map((line) => (
              <div key={line} className="inv-pdf__company-line">{line}</div>
            ))}
            {company?.contactNumber && <div className="inv-pdf__company-line">{company.contactNumber}</div>}
            {company?.email && <div className="inv-pdf__company-line">{company.email}</div>}
            {company?.website && <div className="inv-pdf__company-line">{company.website}</div>}
          </div>
        </div>
        <div>
          <div className="inv-pdf__doc-number">Invoice #{invoiceNumber}</div>
          {invoice?.title && <div className="inv-pdf__doc-title">{invoice.title}</div>}
        </div>
      </header>

      <div className="inv-pdf__divider" />

      {/* ── BILL TO + DATES ── */}
      <div className="inv-pdf__info-row">
        <div className="inv-pdf__bill-to">
          <div className="inv-pdf__info-label">Bill to:</div>
          {clientName && <div className="inv-pdf__bill-name">{clientName}</div>}
          {billingLines.map((line) => (
            <div key={`bill-${line}`} className="inv-pdf__bill-line">{line}</div>
          ))}
          {!billingLines.length &&
            shippingLines.map((line) => (
              <div key={`ship-${line}`} className="inv-pdf__bill-line">{line}</div>
            ))}
          {invoice?.notes && (
            <div className="inv-pdf__notes" dangerouslySetInnerHTML={{ __html: invoice.notes }} />
          )}
        </div>
        <div className="inv-pdf__meta-dates">
          <div className="inv-pdf__meta-row">
            <span className="inv-pdf__meta-label">Date of Issue:</span>
            <span>{formatDate(createdDate)}</span>
          </div>
          {invoice?.dueDate && (
            <div className="inv-pdf__meta-row">
              <span className="inv-pdf__meta-label">Due Date:</span>
              <span>{formatDate(invoice.dueDate)}</span>
            </div>
          )}
          {invoice?.projectName && (
            <div className="inv-pdf__meta-row">
              <span className="inv-pdf__meta-label">Project:</span>
              <span>{invoice.projectName}</span>
            </div>
          )}
          {depositDue > 0 && (
            <div className="inv-pdf__meta-row inv-pdf__meta-row--balance">
              <span className="inv-pdf__meta-label">Deposit Due:</span>
              <span>{fmt(depositDue)}</span>
            </div>
          )}
          {summary.remaining > 0 && (
            <div className="inv-pdf__meta-row inv-pdf__meta-row--balance">
              <span className="inv-pdf__meta-label">Balance Due:</span>
              <span>{fmt(summary.remaining)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="inv-pdf__divider" />

      {/* ── LINE ITEMS ── */}
      <section className="inv-pdf__items">
        <table className="inv-pdf__table">
          <thead>
            <tr>
              {showItemImages && <th className="inv-pdf__col-img" />}
              <th>Description</th>
              {showRoomColumn && <th className="inv-pdf__col-room">Room</th>}
              <th className="inv-pdf__col-num">Qty</th>
              <th className="inv-pdf__col-num">Unit</th>
              <th className="inv-pdf__col-money">Unit Price</th>
              <th className="inv-pdf__col-money">Total</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item, index) => {
              const qty = Number(item?.qty || 0);
              const unitPrice = Number(item?.clientUnit || 0);
              // Freight and tariff only appear in the summary, so the line is merchandise only.
              const lineTotal = lineMerchandise(item);
              const itemImage = getImageUrl(item);

              return (
                <tr key={item?.itemId || `${item?.name || 'item'}-${index}`}>
                  {showItemImages && (
                    <td className="inv-pdf__img-cell">
                      {itemImage ? (
                        <img className="inv-pdf__item-img" src={proxyImg(itemImage, apiBase) || itemImage} alt="" crossOrigin="anonymous" />
                      ) : (
                        <div className="inv-pdf__img-placeholder" />
                      )}
                    </td>
                  )}
                  <td>
                    <div className="inv-pdf__item-name">
                      {item?.clientName || item?.name || item?.description || 'Line item'}
                    </div>
                    {item?.description && item.description !== (item?.clientName || item?.name) && (
                      <div className="inv-pdf__item-desc">{item.description}</div>
                    )}
                  </td>
                  {showRoomColumn && <td className="inv-pdf__col-room">{item?.room || item?.location || '-'}</td>}
                  <td className="inv-pdf__col-num">{qty.toFixed(2)}</td>
                  <td className="inv-pdf__col-num">{item?.unitType || item?.unit || 'Each'}</td>
                  <td className="inv-pdf__col-money">{fmt(unitPrice)}</td>
                  <td className="inv-pdf__col-money">{fmt(lineTotal)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {/* ── SUMMARY ── */}
      <section className="inv-pdf__totals">
        <div className="inv-pdf__totals-box">
          <div className="inv-pdf__totals-sep" />
          <div className="inv-pdf__totals-row">
            <span>Sub Total</span>
            <span>{fmt(summary.merchandise)}</span>
          </div>
          {summary.freightTaxable > 0 && (
            <div className="inv-pdf__totals-row">
              <span>Freight (Taxable)</span>
              <span>{fmt(summary.freightTaxable)}</span>
            </div>
          )}
          {summary.freightNonTaxable > 0 && (
            <div className="inv-pdf__totals-row">
              <span>Freight (Non-Taxable)</span>
              <span>{fmt(summary.freightNonTaxable)}</span>
            </div>
          )}
          {summary.tariffTaxable > 0 && (
            <div className="inv-pdf__totals-row">
              <span>Tariff (Taxable)</span>
              <span>{fmt(summary.tariffTaxable)}</span>
            </div>
          )}
          {summary.tariffNonTaxable > 0 && (
            <div className="inv-pdf__totals-row">
              <span>Tariff (Non-Taxable)</span>
              <span>{fmt(summary.tariffNonTaxable)}</span>
            </div>
          )}
          <div className="inv-pdf__totals-row">
            <span>Sales Tax</span>
            <span>{fmt(summary.tax)}</span>
          </div>
          <div className="inv-pdf__totals-row">
            <span>Total</span>
            <span>{fmt(summary.total)}</span>
          </div>
          {depositRequested > 0 && (
            <div className="inv-pdf__totals-row">
              <span>{depositRequestLabel(invoice?.depositRequest)}</span>
              <span>{fmt(depositRequested)}</span>
            </div>
          )}
          <div className="inv-pdf__totals-row">
            <span>Payment Applied</span>
            <span>{fmt(summary.paid)}</span>
          </div>
          <div className="inv-pdf__totals-sep inv-pdf__totals-sep--brand" />
          <div className="inv-pdf__totals-row inv-pdf__totals-row--balance">
            <span>Balance Due</span>
            <span>{fmt(summary.remaining)}</span>
          </div>
        </div>
      </section>

      {termsText && (
        <section className="inv-pdf__terms">
          <div className="inv-pdf__terms-label">Terms &amp; Conditions</div>
          <div className="inv-pdf__terms-text">{termsText}</div>
        </section>
      )}

      <div className="inv-pdf__footer">{footerText}</div>
    </div>
  );
}

InvoicePdfDocument.propTypes = {
  invoice: PropTypes.object.isRequired,
  company: PropTypes.shape({
    companyName: PropTypes.string,
    address: PropTypes.string,
    contactNumber: PropTypes.string,
    email: PropTypes.string,
    website: PropTypes.string,
    logoUrl: PropTypes.string,
  }),
  apiBase: PropTypes.string,
  template: PropTypes.shape({
    accentColor: PropTypes.string,
    footerText: PropTypes.string,
    termsText: PropTypes.string,
    showRoomColumn: PropTypes.bool,
    showItemImages: PropTypes.bool,
  }),
};
